import { useEffect, useState, useRef } from 'react'
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline'
import { parseDate } from 'utils'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import ButtonComponent from 'components/common/ButtonComponent'
import TableComponent from 'components/common/TableComponent'
import ModalComponent from 'components/common/ModalComponent'
import { useAuth } from 'auth/AuthContext'
import { DateTime } from 'luxon'

export default function TimingDataEdit () {
  const hasFetched = useRef(false)
  const currentFilterDate = useRef(null)
  const { user } = useAuth()
  const [state, setState] = useState({
    workdays: [],
    currentDate: parseDate(new Date()),
    filterDate: DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd'),
    searchTerm: '',
    showEditModal: false,
    selectedWorkday: null
  })

  const getWorkdays = async (filterDate) => {
    try {
      const date = filterDate ?? state.filterDate
      const response = await makeRequest({
        method: 'GET',
        url: `/management/workdays/${user.farmId}?filterDate=${new Date(date).toISOString()}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      setState(s => ({ ...s, workdays: response.data }))
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const openEditModal = (row) => {
    setState(s => ({ ...s, showEditModal: true, selectedWorkday: row }))
  }

  const closeEditModal = () => {
    setState(s => ({ ...s, showEditModal: false, selectedWorkday: null }))
  }

  const handleSaveWorkday = async (formData) => {
    try {
      await makeRequest({
        method: 'PATCH',
        url: `/management/workdays/${state.selectedWorkday.workdayId}`,
        data: {
          startTime: formData.startTime || undefined,
          endTime: formData.endTime || undefined
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      showAlert({ text: 'Workday updated', icon: 'success' })
      closeEditModal()
      await getWorkdays()
    } catch (error) {
      showAlert({ text: 'Failed to update workday', icon: 'error' })
      console.error('Error updating workday:', error)
    }
  }

  const filterWorkdays = () => {
    return state.workdays.filter(w =>
      (w.workerName ?? '').toLowerCase().includes(state.searchTerm)
    )
  }

  useEffect(() => {
    if (hasFetched.current && currentFilterDate.current === state.filterDate) return
    hasFetched.current = true
    currentFilterDate.current = state.filterDate
    getWorkdays(state.filterDate)
  }, [state.filterDate])

  return (
    <div className='p-6 bg-white shadow-md border border-gray-300 border-t-0'>
      <div className='section-header'>
        <div className='header-left'>
          <div className='date-filter'>
            <span>Today ({state.currentDate})</span>
          </div>
        </div>
        <div className='flex items-center gap-4 mb-6'>
          <ButtonComponent className='flex items-center gap-2'>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>Export as Excel</span>
          </ButtonComponent>
          <ButtonComponent className='flex items-center gap-2'>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>Export as PDF</span>
          </ButtonComponent>
        </div>
      </div>

      <div className='search-container flex gap-4 mb-4'>
        <input
          type='text'
          placeholder='Search worker'
          value={state.searchTerm}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='w-48 mt-1 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
        <input
          type='date'
          value={state.filterDate}
          onChange={e => setState(s => ({ ...s, filterDate: e.target.value }))}
          className='w-40 mt-2 mb-2 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
      </div>

      <div className='data-table'>
        <TableComponent
          tableData={{
            columns: [
              { key: 'workerName', label: 'Worker' },
              { key: 'date', label: 'Date', cell: (value) => parseDate(value) },
              { key: 'startTime', label: 'Start Time' },
              { key: 'endTime', label: 'End Time' },
              { key: 'edited', label: 'Edited', cell: (value) => value ? 'Yes' : 'No' },
              { key: 'actions', label: 'Manage' }
            ],
            rows: filterWorkdays()
          }}
          handleEdit={openEditModal}
          defaultSortColumn='workerName'
        />
      </div>

      {state.showEditModal && (
        <ModalComponent
          title='Edit Attendance Record'
          isOpen={state.showEditModal}
          onClose={closeEditModal}
          onSave={handleSaveWorkday}
          initialData={{
            startTime: state.selectedWorkday?.startTime ?? '',
            endTime: state.selectedWorkday?.endTime ?? ''
          }}
        >
          {({ formData, handleChange }) => (
            <form className='space-y-4 w-64' autoComplete='off'>
              <p className='text-sm text-gray-600'>
                Worker: <strong>{state.selectedWorkday?.workerName}</strong>
              </p>
              <label className='block text-sm font-medium'>
                Start Time
                <input
                  type='time'
                  name='startTime'
                  value={formData?.startTime ?? ''}
                  onChange={handleChange}
                  className='w-full mt-1 p-2 border rounded'
                />
              </label>
              <label className='block text-sm font-medium'>
                End Time
                <input
                  type='time'
                  name='endTime'
                  value={formData?.endTime ?? ''}
                  onChange={handleChange}
                  className='w-full mt-1 p-2 border rounded'
                />
              </label>
            </form>
          )}
        </ModalComponent>
      )}
    </div>
  )
}

